import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Bell, BellOff, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  alertNew,
  notificationPermission,
  playAlertSound,
  requestNotificationPermission,
} from "@/lib/salesAlerts";

function threadKey(t) {
  return `${t.id}:${t.last_at || ""}:${t.last_message || ""}:${t.unread ? 1 : 0}`;
}

export default function SalesLiveAlerts() {
  const [perm, setPerm] = useState(() => notificationPermission());
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem("sales-notify-dismiss") === "1"; } catch { return false; }
  });
  const waSeen = useRef(null);
  const leadSeen = useRef(null);
  const ready = useRef(false);

  useEffect(() => {
    setPerm(notificationPermission());
  }, []);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const [wa, crm, reminders] = await Promise.all([
          api.get("/sales/whatsapp/threads", { params: { tag: "inbox" } }),
          api.get("/sales/crm/leads", { params: { page: 1, limit: 25 } }),
          api.get("/sales/pipeline/reminders").catch(() => ({ data: { items: [] } })),
        ]);
        if (!alive) return;
        for (const r of reminders.data?.items || []) {
          alertNew({
            kind: "lead",
            title: "Follow-up today",
            body: `${r.name || r.phone || "Client"} · ${r.company_name || "Pipeline"}`,
            href: "/sales/crm",
          });
        }
        const threads = wa.data?.items || [];
        const leads = crm.data?.items || [];
        const waMap = Object.fromEntries(threads.map((t) => [t.id, threadKey(t)]));
        const leadIds = new Set(leads.map((l) => l.id));

        if (!ready.current) {
          waSeen.current = waMap;
          leadSeen.current = leadIds;
          ready.current = true;
          return;
        }

        for (const t of threads) {
          const prev = waSeen.current[t.id];
          const next = threadKey(t);
          if (!prev) {
            if (t.unread || !t.owner_id || t.tag === "unassigned") {
              alertNew({
                kind: "wa",
                title: "New WhatsApp message",
                body: `${t.name || t.phone}: ${t.last_message || "New chat"}`,
                href: "/sales/whatsapp",
              });
            }
          } else if (prev !== next && t.unread) {
            alertNew({
              kind: "wa",
              title: "New WhatsApp message",
              body: `${t.name || t.phone}: ${t.last_message || "New message"}`,
              href: "/sales/whatsapp",
            });
          }
        }
        waSeen.current = { ...waSeen.current, ...waMap };

        for (const l of leads) {
          if (!leadSeen.current.has(l.id)) {
            alertNew({
              kind: "lead",
              title: "New CRM lead",
              body: `${l.name || l.phone || l.email || "Lead"} · ${l.source || "crm"}`,
              href: "/sales/crm",
            });
          }
        }
        leadSeen.current = new Set([...leadSeen.current, ...leadIds]);
      } catch {
        /* keep polling */
      }
    };
    tick();
    const id = setInterval(tick, 2000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const allow = async () => {
    playAlertSound();
    const p = await requestNotificationPermission();
    setPerm(p);
  };

  const hide = () => {
    setDismissed(true);
    try { sessionStorage.setItem("sales-notify-dismiss", "1"); } catch { /* ignore */ }
  };

  const showBanner = !dismissed && perm !== "granted" && perm !== "unsupported";

  return (
    <>
      {showBanner && (
        <div className="rounded-xl border border-amber-400/40 bg-amber-500/15 text-amber-50 px-4 py-3 mb-4 flex flex-wrap items-center gap-3" data-testid="sales-notify-prompt">
          <Bell className="w-5 h-5 text-amber-300 shrink-0" />
          <div className="flex-1 min-w-[200px] text-sm">
            <div className="font-semibold text-white">Allow browser notifications</div>
            <p className="text-amber-100/90 text-xs mt-0.5">
              Chrome and other browsers need your permission to alert you for each new WhatsApp message and each new CRM lead — even when this tab is in the background.
            </p>
          </div>
          <Button type="button" className="bg-amber-400 text-slate-900 hover:bg-amber-300" onClick={allow} data-testid="sales-notify-allow">
            Allow notifications
          </Button>
          <button type="button" className="p-1 text-amber-200 hover:text-white" onClick={hide} aria-label="Dismiss" data-testid="sales-notify-dismiss"><X className="w-4 h-4" /></button>
        </div>
      )}
      {perm === "denied" && !showBanner && (
        <button type="button" className="mb-3 text-xs text-slate-400 hover:text-amber-200 inline-flex items-center gap-1" onClick={allow}>
          <BellOff className="w-3.5 h-3.5" /> Notifications blocked in the browser — enable them in site settings
        </button>
      )}
    </>
  );
}
